import axios from "axios";
import { t } from "@/locales/i18n";
import { isFunction } from "@/utils/general";
import type { RequestClient } from "./request-client";
import { isSessionExpiredError } from "./session-expired";
import type { MakeErrorMessageFn, ResponseInterceptorConfig } from "./types";

export const defaultResponseInterceptor = ({
  codeField = "code",
  dataField = "data",
  successCode = 0,
}: {
  /** 响应数据中代表访问结果的字段名 */
  codeField: string;
  /** 响应数据中装载实际数据的字段名，或者提供一个函数从响应数据中解析需要返回的数据 */
  dataField: ((response: any) => any) | string;
  /** 当codeField所指定的字段值与successCode相同时，代表接口访问成功。如果提供一个函数，则返回true代表接口访问成功 */
  successCode: ((code: any) => boolean) | number | string;
}): ResponseInterceptorConfig => {
  return {
    fulfilled: (response) => {
      const { config, data: responseData, status } = response;

      if (config.responseFormat === "raw") {
        return response;
      }

      if (status >= 200 && status < 400) {
        if (config.responseFormat === "body") {
          return responseData;
        }
        if (isFunction(successCode) ? successCode(responseData[codeField]) : responseData[codeField] === successCode) {
          return isFunction(dataField) ? dataField(responseData) : responseData[dataField];
        }
      }
      throw Object.assign({}, response, { response });
    },
  };
};

export const authenticateResponseInterceptor = ({
  client,
  onSessionExpired,
  isSessionExpired = () => false,
  doRefreshToken,
  enableRefreshToken,
  formatToken,
}: {
  client: RequestClient;
  onSessionExpired: () => void | Promise<void>;
  isSessionExpired?: () => boolean;
  doRefreshToken: () => Promise<string>;
  enableRefreshToken: boolean;
  formatToken: (token: string) => null | string;
}): ResponseInterceptorConfig => {
  let refreshPromise: Promise<string> | null = null;
  async function expireSession(error: any): Promise<never> {
    error.sessionExpired = true;
    await onSessionExpired();
    throw error;
  }

  return {
    rejected: async (error) => {
      const { config, response } = error;
      // 如果不是 401 错误，直接抛出异常
      if (response?.status !== 401) {
        throw error;
      }
      // 服务端会话已失效时续签无效，交由当前 Provider 处理认证终局。
      if (isSessionExpiredError(error) || isSessionExpired() || !enableRefreshToken || config?.__isRetryRequest) {
        return expireSession(error);
      }
      if (!refreshPromise) {
        // 并发 401 共用一次续签；失败时所有请求直接结束，不能拿空令牌重发。
        refreshPromise = Promise.resolve()
          .then(doRefreshToken)
          .finally(() => {
            refreshPromise = null;
          });
      }
      let newToken: string;
      try {
        newToken = await refreshPromise;
      } catch {
        return expireSession(error);
      }
      if (!newToken || isSessionExpired()) return expireSession(error);
      // 包括排队请求在内，每个请求最多重发一次。
      config.__isRetryRequest = true;
      config.headers.Authorization = formatToken(newToken);
      return client.request(config.url, { ...config });
    },
  };
};

export const errorMessageResponseInterceptor = (makeErrorMessage?: MakeErrorMessageFn): ResponseInterceptorConfig => {
  return {
    rejected: (error: any) => {
      if (axios.isCancel(error)) {
        return Promise.reject(error);
      }

      const err: string = error?.toString?.() ?? "";
      let errMsg = "";
      if (err?.includes("Network Error")) {
        errMsg = t("fallback.http.networkError");
      } else if (error?.message?.includes?.("timeout")) {
        errMsg = t("fallback.http.requestTimeout");
      }
      if (errMsg) {
        makeErrorMessage?.(errMsg, error);
        return Promise.reject(error);
      }

      let errorMessage = "";
      const status = error?.response?.status;

      switch (status) {
        case 400: {
          errorMessage = t("fallback.http.badRequest");
          break;
        }
        case 401: {
          errorMessage = t("fallback.http.unauthorized");
          break;
        }
        case 403: {
          errorMessage = t("fallback.http.forbidden");
          break;
        }
        case 404: {
          errorMessage = t("fallback.http.notFound");
          break;
        }
        case 408: {
          errorMessage = t("fallback.http.requestTimeout");
          break;
        }
        default: {
          errorMessage = t("fallback.http.internalServerError");
        }
      }
      makeErrorMessage?.(errorMessage, error);
      return Promise.reject(error);
    },
  };
};
